import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDisplayDate, formatDisplayDateTime } from '@/lib/date-format';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date | null | undefined): string {
  return formatDisplayDate(date);
}

export function formatDateTime(date: string | Date | null | undefined): string {
  return formatDisplayDateTime(date);
}

export function formatActivityTimestamp(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const now = new Date();
  const then = new Date(date);
  if (isNaN(then.getTime())) return '—';

  const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  const time = timeFmt.format(then);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfThen.getTime()) / 86400000);

  if (dayDiff === 0) return `Today at ${time}`;
  if (dayDiff === 1) return `Yesterday at ${time}`;

  return `${formatDisplayDate(then)}, ${time}`;
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const now = new Date();
  const then = new Date(date);
  if (isNaN(then.getTime())) return '—';
  const diff = now.getTime() - then.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return formatDisplayDate(date);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'text-red-500';
    case 'high':
      return 'text-orange-500';
    case 'medium':
      return 'text-yellow-500';
    case 'low':
      return 'text-blue-500';
    default:
      return 'text-muted-foreground';
  }
}

export function getPriorityBgColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'bg-red-500/10 text-red-500';
    case 'high':
      return 'bg-orange-500/10 text-orange-500';
    case 'medium':
      return 'bg-yellow-500/10 text-yellow-500';
    case 'low':
      return 'bg-blue-500/10 text-blue-500';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/**
 * Get solid background color for priority level.
 */
export function getPriorityHexColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return '#ef4444';
    case 'high':
      return '#f97316';
    case 'medium':
      return '#eab308';
    case 'low':
      return '#3b82f6';
    default:
      return '#6b7280';
  }
}

/**
 * Calculate relative luminance of a hex color.
 * Returns a value between 0 (black) and 1 (white).
 */
export function getColorLuminance(hex: string): number {
  const color = hex.replace('#', '');
  const r = parseInt(color.slice(0, 2), 16) / 255;
  const g = parseInt(color.slice(2, 4), 16) / 255;
  const b = parseInt(color.slice(4, 6), 16) / 255;

  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Get readable text color (white or dark) for a given background color.
 * Uses WCAG luminance contrast guidelines.
 */
export function getContrastTextColor(bgColor: string | null | undefined): string {
  if (!bgColor) return '#ffffff';
  try {
    const luminance = getColorLuminance(bgColor);
    // Use dark text for bright backgrounds (luminance > 0.5)
    return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
  } catch {
    return '#ffffff';
  }
}
